'use client';

/**
 * Cross-brand switcher + explainer shown in the dancer shell. Lets the user
 * jump between Flow and Ballee via a single-use Supabase magic-link generated
 * server-side, and (in the `card`/`hero` variants) quickly explains what the
 * other brand is. Opens the link in a NEW TAB so the current session is
 * preserved if anything fails on the target side.
 *
 * Variants:
 *  - `icon` — icon-only button (collapsed sidebar rail).
 *  - `card` — compact branded panel (sidebar footer, mobile menu footer).
 *  - `hero` — full-width branded panel with headline (dancer homepage).
 *
 * See:
 *  - apps/web/app/home/(user)/_lib/server/request-cross-brand-switch-action.ts
 *  - apps/web/lib/brand.ts (Brand, BRAND_CONFIG, getOtherBrand)
 *  - docs/architecture/domain-and-seo-architecture.md (why host-scoped cookies)
 */
import { useTransition } from 'react';
import { ArrowRight, ArrowRightLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@kit/analytics';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

import { BalleeLogoHeader } from '~/components/brand/ballee-logo';
import { FlowLogoHeader } from '~/components/brand/flow-logo';
import { type Brand } from '~/lib/brand';
import { requestCrossBrandSwitchAction } from '../_lib/server/request-cross-brand-switch-action';

type BrandSwitcherVariant = 'icon' | 'card' | 'hero';

// Per-brand accent used for the subtle panel wash. Both brands live in the
// same warm coral family, so the panel reads as "branded" without clashing
// with the neutral app chrome. Alpha suffixes: ~12% top, ~3% bottom.
const BRAND_ACCENT: Record<Brand, string> = {
  flow: '#bf5f60',
  ballee: '#e07a5f',
};

export function BrandSwitcherWidget({
  currentBrand,
  variant = 'card',
}: {
  currentBrand: Brand;
  variant?: BrandSwitcherVariant;
}) {
  const { t } = useTranslation('common');
  const [pending, startTransition] = useTransition();
  const targetBrand: Brand = currentBrand === 'flow' ? 'ballee' : 'flow';
  const targetLabel = t(`dancerHome.brands.${targetBrand}`);
  const currentLabel = t(`dancerHome.brands.${currentBrand}`);

  function handleSwitch() {
    void analytics.trackEvent('cross_brand_switch_clicked', {
      from: currentBrand,
      to: targetBrand,
    });

    startTransition(async () => {
      const result = await requestCrossBrandSwitchAction({ targetBrand });

      if (!result.success) {
        if (result.error === 'RATE_LIMITED') {
          toast.error(t('dancerHome.switcher.rateLimited'));
        } else {
          toast.error(t('dancerHome.switcher.genericError'));
        }
        void analytics.trackEvent('cross_brand_switch_failed', {
          from: currentBrand,
          to: targetBrand,
          error_code: result.error,
        });
        return;
      }

      // Open target host in a NEW TAB. The action_link is a single-use,
      // short-lived Supabase magic-link that lands on the target's
      // /auth/callback?next=/home. Failures on the target side don't
      // disturb the current Flow/Ballee session in this tab.
      window.open(result.url, '_blank', 'noopener,noreferrer');
    });
  }

  const switchToLabel = t('dancerHome.switcher.switchTo', {
    brand: targetLabel,
  });
  const currentBrandLabel = t('dancerHome.switcher.currentBrand', {
    brand: currentLabel,
  });
  const tagline = t(`dancerHome.flowPromo.${targetBrand}.tagline`);
  const headline = t(`dancerHome.flowPromo.${targetBrand}.headline`);

  if (variant === 'icon') {
    // Collapsed sidebar rail: render just the icon button.
    return (
      <Button
        variant="ghost"
        size="icon"
        className="mx-auto my-2 h-9 w-9"
        onClick={handleSwitch}
        disabled={pending}
        aria-label={switchToLabel}
        title={switchToLabel}
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ArrowRightLeft className="h-4 w-4" />
        )}
      </Button>
    );
  }

  const accent = BRAND_ACCENT[targetBrand];
  const TargetLogo = targetBrand === 'flow' ? FlowLogoHeader : BalleeLogoHeader;
  const isHero = variant === 'hero';

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border',
        isHero ? 'p-6 md:p-8' : 'p-4',
      )}
      style={{
        backgroundImage: `linear-gradient(135deg, ${accent}1f, ${accent}08)`,
        borderColor: `${accent}33`,
      }}
      aria-label={currentBrandLabel}
    >
      <div
        className={cn(
          isHero &&
            'flex flex-col gap-5 md:flex-row md:items-center md:justify-between',
        )}
      >
        <div className={cn('space-y-2', isHero && 'max-w-xl')}>
          <TargetLogo size={isHero ? 'sm' : 'xs'} />
          {isHero && (
            <h3 className="text-foreground text-lg font-semibold">
              {headline}
            </h3>
          )}
          <p
            className={cn(
              'text-muted-foreground',
              isHero ? 'text-sm' : 'text-xs',
            )}
          >
            {tagline}
          </p>
        </div>

        <Button
          size={isHero ? 'default' : 'sm'}
          onClick={handleSwitch}
          disabled={pending}
          className={cn('shrink-0', isHero ? 'md:w-auto' : 'mt-3 w-full')}
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          {switchToLabel}
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
